import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

import dayjs from "dayjs";
import { WHATSAPP_META_CARD_ENDPOINT } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import AppTable from "../../common/app-table";
import typography from "../../cssUtils/typography";
import { ColorsConstants, grayFontColor } from "../../cssUtils/constants";

export default function RateCardTab({ refreshKey, dateInput }) {
  const { getValidToken } = useAuth();
  const [loading, setLoading] = useState(false)
  // All transformed country rows (after grouping by country)
  const [allRows, setAllRows] = useState([]);
  const [currency, setCurrency] = useState("");

  const columns = [
    {
      id: "country",
      label: "Country",
      render: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span>{row.country === "India" ? "🇮🇳" : "🌐"}</span>
          <span>{row.country}</span>
        </Box>
      ),
    },
    {
      id: "marketing_rate",
      label: "Marketing Rate",
      render: (row) => `${currencySymbol}${row.marketing || ""}`,
    },
    {
      id: "utility_rate",
      label: "Utility Rate",
      render: (row) => `${currencySymbol}${row.utility || ""}`,
    },
    {
      id: "service_rate",
      label: "Service Rate",
      render: (row) => `${currencySymbol}${row.service || ""}`,
    },
    {
      id: "authentication_rate",
      label: "Authentication Rate",
      render: (row) => `${currencySymbol}${row.authentication || ""}`,
    },
    {
      id: "authentication_international_rate",
      label: "Authentication Intl. Rate",
      render: (row) => `${currencySymbol}${row.authentication_international || ""}`,
    },
  ];

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 20,
  });

  // Function to get currency symbol
  const getCurrencySymbol = (currencyCode) => {
    switch (currencyCode?.toUpperCase()) {
      case "INR":
        return "₹";      // India
      case "SAR":
        return "﷼";      // Saudi Riyal
      case "AED":
        return "د.إ";    // UAE Dirham
      case "ZAR":
        return "R";      // South Africa Rand
      case "MYR":
        return "RM";     // Malaysian Ringgit
      case "IDR":
        return "Rp";     // Indonesian Rupiah
      case "PEN":
        return "S/";     // Peruvian Sol
      case "CLP":
        return "$";      // Chilean Peso
      case "COP":
        return "$";      // Colombian Peso
      case "BRL":
        return "R$";     // Brazilian Real
      case "MXN":
        return "$";      // Mexican Peso
      case "KES":
        return "KSh";    // Kenyan Shilling
      case "ZMW":
        return "ZK";     // Zambian Kwacha
      case "ETB":
        return "Br";     // Ethiopian Birr
      case "CDF":
        return "FC";     // Congolese Franc
      case "GNF":
        return "FG";     // Guinean Franc
      case "SGD":
        return "S$";     // Singapore Dollar
      case "PHP":
        return "₱";
      case "USD":
        return "$";
      case "EUR":
        return "€";
      case "GBP":
        return "£";
      default:
        return currencyCode ?? "";
    }
  };

  const fetchMetaRateData = async () => {
    let searchDateURL = "";
    if (dateInput) {
      let dateString = dayjs(dateInput).format("YYYY-MM-DD");
      searchDateURL = `&search_date=${dateString}`;
    }

    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    const headers = {
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
      Authorization: `Bearer ${token}`,
    };

    try {
      // Single API call — fetch all records at once
      const response = await fetch(
        `${WHATSAPP_META_CARD_ENDPOINT}?limit=1000&offset=0${searchDateURL}`,
        { headers }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch rate data");
      }
      const jsonData = await response.json();

      console.log("GET Meta Rate Data: ", jsonData);

      const currencyCode = jsonData?.currency ?? "";
      setCurrency(currencyCode);

      const transformed = transformRateData(jsonData.results ?? [], currencyCode);
      setAllRows(Object.values(transformed));

      // Reset to first page on every fresh fetch
      setController((prev) => ({ ...prev, page: 0 }));
    } catch (error) {
      console.error("Error fetching rate data:", error.message);
    }
  };

  useEffect(() => {
    fetchMetaRateData();
  }, [refreshKey, dateInput]); // no page/rowsPerPage deps — pagination is now FE only

  const transformRateData = (data, currencyCode) => {
    if (!data || data.length === 0) return {};

    const rateObj = {};
    console.log(data, "data for meta rate card");
    data.forEach((item) => {
      const country = item?.country;
      if (!country) return; // skip records with no country — prevents empty rows

      const messageType = item?.message_type?.toLowerCase();
      const description = item?.description;

      // Initialize country object if it doesn't exist
      if (!rateObj[country]) {
        rateObj[country] = {
          country: country,
          currency: currencyCode ?? "INR",
          marketing: "",
          marketing_desc: "",
          utility: "",
          utility_desc: "",
          service: "",
          service_desc: "",
          authentication: "",
          authentication_desc: "",
          authentication_international: "",
          authentication_international_desc: "",
        };
      }

      // Fill fields based on message type
      switch (messageType) {
        case "marketing":
          rateObj[country].marketing = item.rate;
          rateObj[country].marketing_desc = item.description ?? "";
          break;
        case "utility":
          rateObj[country].utility = item.rate;
          rateObj[country].utility_desc = item.description ?? "";
          break;
        case "service":
          rateObj[country].service = item.rate;
          rateObj[country].service_desc = item.description ?? "";
          break;
        case "authentication":
          rateObj[country].authentication = item.rate;
          rateObj[country].authentication_desc = item.description ?? "";
          break;
        case "authentication_intl":
          rateObj[country].authentication_international = item.rate;
          rateObj[country].authentication_international_desc =
            item.description ?? "";
          break;
        default:
          console.warn("Unknown message_type:", messageType);
      }
    });

    return rateObj;
  };

  // FE pagination handlers
  const handlePageChange = (event, newPage) => {
    setController((prev) => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    const newRows = parseInt(event.target.value, 20);
    setController({ page: 0, rowsPerPage: newRows });
  };

  // Slice the full dataset for the current page
  const paginatedRows = allRows.slice(
    controller.page * controller.rowsPerPage,
    controller.page * controller.rowsPerPage + controller.rowsPerPage
  );

  const currencySymbol = getCurrencySymbol(currency);
  // Used by the Rate Information description section below
  const transformedData = allRows[0] ?? {};
  console.log(allRows, "transformed data for meta rate card");

  return (
    <Box sx={{ pt: 5, px: 3, backgroundColor: "#F7F7F7" }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column", // stack heading + subtext
              alignItems: "flex-start",
              mb: 3,
              gap: 0.5, // small spacing between heading & subtext
            }}
          >
            <Typography sx={{ ...typography.h1 }}>Message Rates by Country</Typography>
            <Typography sx={{ ...typography.h2, color: ColorsConstants.primaryGray50 }} variant="body2" color="text.secondary">
              The billing currency is {currencySymbol}
            </Typography>
          </Box>

          {/* <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: "8px"}}>
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align="left"
                      style={{
                        minWidth: column.minWidth,
                        border: "1px solid rgba(224, 224, 224, 1)",
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((data) => (
                  <TableRow key={data.country}>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <span>{data.country === "India" ? "🇮🇳" : "🌐"}</span>
                        <span>{data.country}</span>
                      </Box>
                    </TableCell>
                    <TableCell>{currencySymbol}{data.marketing || ""}</TableCell>
                    <TableCell>{currencySymbol}{data.utility || ""}</TableCell>
                    <TableCell>{currencySymbol}{data.service || ""}</TableCell>
                    <TableCell>{currencySymbol}{data.authentication || ""}</TableCell>
                    <TableCell>
                      {currencySymbol}{data.authentication_international || ""}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <CustomPagination
            count={allRows.length}        // total country rows — FE drives this now
            page={controller.page}
            rowsPerPage={controller.rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[20, 50, 100]}
          /> */}

          <AppTable
            columns={columns}
            rows={paginatedRows}
            rowKey="country"
            loading={loading}
            emptyMessage="No rate data available"
            maxHeight={600}
            hideMainScroll={false}
            paperSx={{ border: "1px solid #DCDFE5", borderRadius: "12px", mb: 2 }}
            containerSx={{
              scrollbarWidth: "thin",
              scrollbarColor: "#C5CEDB #F3F6FB",
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-track": { backgroundColor: "#F3F6FB", borderRadius: "8px" },
              "&::-webkit-scrollbar-thumb": { backgroundColor: "#C5CEDB", borderRadius: "8px" },
              "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#A8B4C7" },
            }}
            // Pagination wired through AppTable
            count={allRows.length}
            page={controller.page}
            rowsPerPage={controller.rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography sx={{ ...typography.h1 }} gutterBottom>
            Rate Information
          </Typography>
          <Grid container direction="column" spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: "primary.50" }}>
                <Typography
                  variant="subtitle1"
                  color="primary.dark"
                  fontWeight="medium"
                  gutterBottom
                  sx={{ ...typography.h2 ,color: "#1565c0", fontWeight: 500}}
                >
                  Marketing Messages
                </Typography>
                <Typography sx={{ ...typography.bodyLarge, fontWeight: 500}}>
                  {transformedData.marketing_desc || "No description available"}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: "success.50" }}>
                <Typography
                  variant="subtitle1"
                  color="success.dark"
                  fontWeight="medium"
                  gutterBottom
                  sx={{ ...typography.h2 ,color: "#1b5e20", fontWeight: 500}}
                >
                  Utility Messages
                </Typography>
                <Typography sx={{ ...typography.bodyLarge, fontWeight: 500}} color="success.700">
                  {transformedData.utility_desc || "No description available"}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: "warning.50" }}>
                <Typography
                  variant="subtitle1"
                  color="warning.dark"
                  fontWeight="medium"
                  gutterBottom
                  sx={{ ...typography.h2 ,color: "#e65100", fontWeight: 500}}
                >
                  Service Messages
                </Typography>
                <Typography sx={{ ...typography.bodyLarge, fontWeight: 500}}>
                  {transformedData.service_desc || "No description available"}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: "secondary.50" }}>
                <Typography
                  variant="subtitle1"
                  color="secondary.dark"
                  fontWeight="medium"
                  gutterBottom
                  sx={{ ...typography.h2 ,color: "#7b1fa2", fontWeight: 500}}
                >
                  Authentication
                </Typography>
                <Typography sx={{ ...typography.bodyLarge, fontWeight: 500}}>
                  {transformedData.authentication_desc ||
                    "No description available"}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: "secondary.50" }}>
                <Typography
                  variant="subtitle1"
                  color="error.dark"
                  fontWeight="medium"
                  gutterBottom
                  sx={{ ...typography.h2 ,color: "#c62828", fontWeight: 500}}
                  
                >
                  Authentication International
                </Typography>
                <Typography sx={{ ...typography.bodyLarge, fontWeight: 500}}>
                  {transformedData.authentication_international_desc ||
                    "No description available"}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}